import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, Users, Clock, Shield, BarChart3, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampaignLanding() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/campaigns/${id}`)
      .then(r => setCampaign(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!campaign) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <p className="text-[#9CA3AF]">Campaign not found.</p>
    </div>
  );

  const c = campaign;
  const spotsLeft = (c.spots_total || 0) - (c.spots_filled || 0);

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="campaign-landing">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-7 w-7" />
            <span className="font-display text-lg font-bold"><span className="text-[#FF4343]">Freddy</span>Leads</span>
          </div>
          <Link to="/login?tab=register" data-testid="landing-apply-btn"
            className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-xs font-semibold hover:bg-[#E03030] transition-all">
            Apply to Join
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 mb-8">
          {c.brand?.logo_url && (
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src={c.brand.logo_url} alt={c.brand_name} className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-[#9CA3AF]">{c.brand_name}</span>
              {c.brand?.verified && <span className="text-[#FF4343] text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Verified</span>}
            </div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold mb-3" data-testid="landing-title">{c.title}</h1>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-mono uppercase tracking-wider px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{c.industry}</span>
              {c.platforms?.map(p => (
                <span key={p} className="text-xs font-mono uppercase tracking-wider px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{p}</span>
              ))}
              <span className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#10B981]">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> {c.status}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-[#111827] border border-white/10 p-5 sm:p-8">
              <p className="text-[#9CA3AF] leading-relaxed mb-6">{c.description}</p>

              {c.talking_points?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" /> What To Share
                  </h3>
                  <ul className="space-y-2">
                    {c.talking_points.map((tp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                        <span className="text-[#FF4343] mt-0.5 flex-shrink-0">-</span> {tp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {c.example_post && (
                <div className="border-t border-white/10 pt-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Example Post</h3>
                  <div className="bg-white/5 p-4 text-sm text-[#9CA3AF] italic leading-relaxed">
                    "{c.example_post}"
                  </div>
                </div>
              )}
            </div>

            {/* Guardrails */}
            {c.guardrails?.length > 0 && (
              <div className="bg-[#111827] border border-white/10 p-5 sm:p-8">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#FF4343]" /> Brand Guidelines
                </h3>
                <ul className="space-y-2">
                  {c.guardrails.map((g, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                      <span className="text-red-400 mt-0.5 flex-shrink-0">-</span> {g}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Compensation Card */}
            <div className="bg-[#111827] border border-[#FF4343]/20 p-5">
              <h3 className="font-display text-lg font-semibold mb-4">Compensation</h3>
              <div className="space-y-3 mb-5">
                {c.flat_fee && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Flat Fee</span>
                    <span className="font-mono text-[#FF4343] font-bold">${Number(c.flat_fee).toLocaleString()}</span>
                  </div>
                )}
                {c.cpm_rate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">CPM Rate</span>
                    <span className="font-mono text-[#FF4343] font-bold">${c.cpm_rate}</span>
                  </div>
                )}
                {c.floor_guarantee && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Floor Guarantee</span>
                    <span className="font-mono text-[#FF4343] font-bold">${Number(c.floor_guarantee).toLocaleString()}</span>
                  </div>
                )}
                {c.performance_bonus_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">Performance Bonus</span>
                    <span className="font-mono text-[#FF4343] font-bold">+${Number(c.performance_bonus_amount).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="border-t border-white/10 pt-4 space-y-2 mb-5">
                <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                  <Users className="w-3.5 h-3.5" /> Required: {c.exec_tier_required || 'Open'}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                  <BarChart3 className="w-3.5 h-3.5" /> Min followers: {c.min_followers?.toLocaleString() || 'None'}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                  <Clock className="w-3.5 h-3.5" /> Deadline: {c.deadline ? new Date(c.deadline).toLocaleDateString() : 'Open'}
                </div>
              </div>
              {spotsLeft > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                    <span>{c.spots_filled || 0} of {c.spots_total || 0} spots filled</span>
                    <span className="text-[#FF4343]">{spotsLeft} left</span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5">
                    <div className="bg-[#FF4343] h-1.5 transition-all" style={{ width: `${((c.spots_filled || 0) / (c.spots_total || 1)) * 100}%` }} />
                  </div>
                </div>
              )}
              <Link to="/login?tab=register" data-testid="landing-apply-cta"
                className="w-full flex items-center justify-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] transition-all">
                Apply for This Campaign <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Powered By */}
            <div className="bg-[#111827] border border-white/10 p-4 text-center">
              <p className="text-[10px] text-[#6B7280] mb-2">Powered by</p>
              <div className="flex items-center justify-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-5 w-5" />
                <span className="font-display text-sm font-bold"><span className="text-[#FF4343]">Freddy</span>Leads</span>
              </div>
              <p className="text-[10px] text-[#6B7280] mt-1">by Freddy Media</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#6B7280]">&copy; 2026 Freddy Media LLC. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/terms" className="text-xs text-[#6B7280] hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="text-xs text-[#6B7280] hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
